import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Box, Grid, Typography, CircularProgress } from "@mui/material";

const WIDGET_SRC =
  process.env.REACT_APP_WIDGET_SRC ||
  "https://d15o5a6138uh8e.cloudfront.net/widget.js";

const ensureWidgetScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("No window"));
    if (window.mountTNFTWidget) return resolve();
    const existing = Array.from(document.getElementsByTagName("script")).find(
      (s) => s.src === WIDGET_SRC
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", (e) => reject(e));
      return;
    }
    const script = document.createElement("script");
    script.src = WIDGET_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.body.appendChild(script);
  });

const CenteredSpinner = () => (
  <Grid
    container
    direction="column"
    alignItems="center"
    justifyContent="center"
    sx={{ minHeight: "60vh" }}
  >
    <CircularProgress />
  </Grid>
);

const Invite = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(null);
  const [eventName, setEventName] = useState("");
  const [widgetFailed, setWidgetFailed] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const server = process.env.REACT_APP_TNFT_SERVER;
        const res = await axios.get(`${server}/unauth/invite/${token}`);
        const resData = res.data;
        if (!resData.success) {
          setInvalid("notFound");
        } else if (!resData.valid) {
          setInvalid(resData.reason);
          setEventName(resData.data?.event?.name || "");
        } else {
          setEventName(resData.data?.event?.name || "");
        }
      } catch (e) {
        setInvalid("notFound");
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [token]);

  useEffect(() => {
    if (loading || invalid) return;
    const mount = async () => {
      try {
        await ensureWidgetScript();
        const el = document.getElementById("tnfm-invite-widget");
        if (el) el.innerHTML = "";
        if (!window.mountTNFTWidget) {
          setWidgetFailed(true);
          return;
        }
        window.mountTNFTWidget("tnfm-invite-widget", "", "#fc3d03", {
          mode: "invite",
          inviteToken: token,
        });
      } catch (e) {
        console.log("[Invite] widget load error", e);
        setWidgetFailed(true);
      }
    };
    mount();
  }, [loading, invalid, token]);

  if (loading) return <CenteredSpinner />;

  if (invalid) {
    const COPY = {
      notFound: {
        title: "This link is not valid",
        body: "Check the link in your email, or get in touch with the organizer.",
      },
      expired: {
        title: "This invitation has expired",
        body: "Contact the organizer if you would still like to attend.",
      },
      canceled: {
        title: "This invitation was withdrawn",
        body: "Contact the organizer if you think this is a mistake.",
      },
      alreadyPaid: {
        title: "You are all set",
        body: "This invitation has already been paid for. Check your email for your confirmation.",
      },
      refunded: {
        title: "This ticket was refunded",
        body: "Your spot has been released and your money returned. Contact the organizer if you would still like to come.",
      },
      eventClosed: {
        title: "This event is no longer open",
        body: "Contact the organizer for details.",
      },
    };
    const copy = COPY[invalid] || COPY.notFound;

    return (
      <Box sx={{ maxWidth: 560, mx: "auto", px: 3, py: 8, textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          {copy.title}
        </Typography>
        {eventName && (
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {eventName}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {copy.body}
        </Typography>
      </Box>
    );
  }

  if (widgetFailed) {
    return (
      <Box sx={{ maxWidth: 560, mx: "auto", px: 3, py: 8, textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          We could not load the checkout
        </Typography>
        {eventName && (
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {eventName}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Your invitation is still valid. Please refresh the page, or get in
          touch with the organizer if this keeps happening.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 720,
        mx: "auto",
        px: 2,
        pt: 3,
        pb: "calc(80px + env(safe-area-inset-bottom))",
      }}
    >
      <div id="tnfm-invite-widget" />
    </Box>
  );
};

export default Invite;
